import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Zap, ArrowRight, LogOut, Factory, Mountain, Building2,
  FileText, FlaskConical, Layers, ShieldCheck, Smartphone,
  Sparkles, FileSpreadsheet, Package, MapPin, Hammer,
  GitBranch, Cog,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import Brand from "../components/Brand";
import ThemeToggle from "../components/ThemeToggle";

const DOMAINS = [
  {
    icon: Factory, accent: "#F26B1F", label: "Process Plant",
    sectors: "Oil & Gas · LNG · Refining · Petrochem · Specialty Chem · Pharma · Food · Water · Power · Hydrogen",
    capabilities: "PFD · P&ID · 3D layout · HMB · Equipment list · ISA-5.1 instruments · HAZOP · Datasheets · DEXPI / IFC export",
  },
  {
    icon: Mountain, accent: "#7AB8FF", label: "Infrastructure",
    sectors: "Roads · Bridges · Highways · Metros · Dams · Tunnels · Airports · Stormwater · Sewerage",
    capabilities: "HCM traffic · Hydraulics · Pavement IRC 37/58 · Bearing & pile capacity · Mass-haul · FHWA noise · Earthwork",
  },
  {
    icon: Building2, accent: "#34A853", label: "Real Estate",
    sectors: "Residential · Commercial · Mixed-use · Hospitality · Logistics · Healthcare · Education",
    capabilities: "FSI · NPV / IRR · BOQ · Daylight · Ventilation · Acoustics · IGBC / GRIHA / LEED · NBC + 7-city DCR",
  },
];

const TIER_FEATURES = [
  {
    icon: FileSpreadsheet, accent: "#F26B1F",
    title: "Tier-1 · Critical deliverables",
    blurb: "BOQ envelope · CPM Gantt · S-curve · Tender DOCX · Calculation sheets · IS 2502 Bar Bending Schedule.",
  },
  {
    icon: MapPin, accent: "#7AB8FF",
    title: "Tier-2 · Differentiator modules",
    blurb: "Survey CSV / LandXML → Delaunay TIN with click-to-place cross-sections · GIS in WGS84 + UTM (KML / GeoJSON / GPX / SHP) · Tech-spec writer · EIA Leopold matrix · Value engineering + LCCA.",
  },
  {
    icon: Sparkles, accent: "#9C7BFB",
    title: "Tier-3 · AI + compliance",
    blurb: "Geotech PDF parsed by Capexara Process Industry AI (20+ correlations) · Green-building scorecards · ISO 19650 BEP + 35 model-coordination rules · AR / VR / 360 tour · NBC 2016 + 7 city DCR auto-checks.",
  },
  {
    icon: GitBranch, accent: "#34A853",
    title: "Closed-loop automation",
    blurb: "Soil → recommended foundation → priced BOQ in 4 clicks. AI-extracted parameters flow into Stage-G calculations and the Tier-1 rate database — no copy-paste.",
  },
  {
    icon: Package, accent: "#FBBC04",
    title: "Issue-For-Construction bundle",
    blurb: "One ZIP, 15 sorted folders: PDFs, XLSX, IFC4, glTF, DXF, DEXPI, JSON manifests, all SHA-256 signed.",
  },
  {
    icon: ShieldCheck, accent: "#E14B4B",
    title: "Standards-aware",
    blurb: "ASME B31.3 · API 650 · ISA 5.1 · ISO 10628 · IS 1893 · IS 2911 · IRC 37/58 · NBC 2016 · ISO 19650 · ATEX / IEC 60079.",
  },
];

const STAGES = [
  { n: "01", t: "INTERPRET", sub: "PDO" },
  { n: "02", t: "PFD", sub: "Stream-balanced" },
  { n: "03", t: "P&ID", sub: "ISA-5.1 + SIS" },
  { n: "04", t: "3D MODEL", sub: "glTF + IFC4" },
  { n: "05", t: "BOQ", sub: "Auto-priced" },
  { n: "06", t: "IFC ZIP", sub: "Construction-ready" },
];

export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="pf-grain" style={{ minHeight: "100vh", background: "var(--pf-bg)" }}>
      {/* Header */}
      <header style={{
        borderBottom: "1px solid var(--pf-border)", position: "sticky",
        top: 0, background: "rgba(10,10,10,0.85)",
        backdropFilter: "blur(10px)", zIndex: 20,
      }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px 28px",
                       display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" data-testid="brand-link" style={{ textDecoration: "none" }}>
            <Brand height={56} testId="landing-brand" />
          </Link>
          <nav style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <ThemeToggle testId="landing-theme-toggle" />
            {user && user.id ? (
              <>
                <Link to="/projects" className="btn btn-ghost" data-testid="nav-projects">Workbench</Link>
                <button className="btn btn-ghost" onClick={() => { logout(); nav("/"); }} data-testid="nav-logout">
                  <LogOut size={14}/>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost" data-testid="nav-login">Sign in</Link>
                <Link to="/register" className="btn btn-primary" data-testid="nav-register">
                  Start building <ArrowRight size={14}/></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ── HERO ────────────────────────────────────────────── */}
      <section style={{ position: "relative", borderBottom: "1px solid var(--pf-border)" }}>
        <div className="pf-blueprint-bg" style={{ position: "absolute", inset: 0, opacity: 0.55 }} />
        <div style={{
          position: "relative", maxWidth: 1400, margin: "0 auto",
          padding: "88px 28px 110px",
          display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 48,
        }}>
          <div>
            <div className="pf-overline" data-testid="hero-overline" style={{ marginBottom: 20 }}>
              PROCESS INDUSTRY · PFD → AS-BUILT · HAZOP-NATIVE
            </div>
            <h1 className="pf-heading" data-testid="hero-title"
                style={{
                  fontSize: "clamp(40px, 6.4vw, 84px)", fontWeight: 900,
                  lineHeight: 0.98, letterSpacing: "-0.045em", margin: 0,
                }}>
              Digital twin of your{" "}
              <span style={{ color: "var(--pf-accent)" }}>plant</span><br/>
              — from PFD concept to{" "}
              <span style={{ color: "var(--pf-accent-2)" }}>as-built handover</span>,
              with <span style={{
                textDecoration: "underline",
                textDecorationColor: "var(--pf-accent)",
                textUnderlineOffset: 8, textDecorationThickness: 4,
              }}>HAZOP baked in</span>.
            </h1>
            <p data-testid="hero-blurb" style={{
              color: "var(--pf-text-muted)", fontSize: 17, maxWidth: 600,
              marginTop: 24, lineHeight: 1.6,
            }}>
              Capexara Process Industry is an agentic AI design engine for
              refineries, petrochemicals, LNG, pharma, water and power plants.
              Describe your unit and receive standards-aware deliverables:
              PFD, P&amp;ID, 3D model, stress analysis, priced MTO, HAZOP
              worksheet and an Issue-For-Construction bundle — all signed,
              all auditable.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
              <Link to="/register" className="btn btn-primary" data-testid="hero-cta-register">
                <Zap size={14}/> Start free build <ArrowRight size={14}/>
              </Link>
              <Link to="/login" className="btn btn-ghost" data-testid="hero-cta-login">
                I have an account
              </Link>
            </div>
            <div data-testid="hero-stats" style={{
              display: "flex", gap: 28, marginTop: 40, flexWrap: "wrap",
            }}>
              <Stat n="1" t="Domain — Process Industry" />
              <Stat n="10+" t="Sectors (refinery · petrochem · LNG · pharma…)" />
              <Stat n="220+" t="API endpoints" />
              <Stat n="500+" t="Tests · 100% green" />
            </div>
          </div>

          {/* Live blueprint preview card */}
          <div style={{ position: "relative" }}>
            <div className="pf-card" style={{
              padding: 16, background: "var(--pf-blueprint)",
              position: "relative", overflow: "hidden", color: "var(--pf-text)",
            }}>
              <div className="pf-overline" style={{ marginBottom: 10, color: "var(--pf-accent-2)" }}>
                LIVE · BLUEPRINT PREVIEW
              </div>
              <PreviewSvg />
              <div style={{
                position: "absolute", right: 16, bottom: 16,
                fontFamily: "JetBrains Mono", fontSize: 10,
                color: "var(--pf-text-muted)",
              }}>
                PROJ-0001 · CRUDE DESALTER · REV A
              </div>
            </div>
            <div style={{
              marginTop: 16, display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)", gap: 6,
            }}>
              {STAGES.map((s) => (
                <div key={s.n} className="pf-step" data-state="complete"
                      data-testid={`hero-step-${s.n}`}>
                  <span className="idx">{s.n}</span>
                  <span className="bullet" />
                  <span className="lbl">{s.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── DOMAINS ────────────────────────────────────────── */}
      <section data-testid="section-domains" style={{ borderBottom: "1px solid var(--pf-border)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "72px 28px" }}>
          <div className="pf-overline" style={{ marginBottom: 14 }}>
            BUILT FOR THE WHOLE CAPITAL-PROJECTS WORLD
          </div>
          <h2 className="pf-heading" style={{
            fontSize: "clamp(28px, 4vw, 44px)", margin: "0 0 32px",
            letterSpacing: "-0.03em",
          }}>
            Three domains. One AI workbench.
          </h2>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))",
            gap: 14,
          }}>
            {DOMAINS.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.label} className="pf-card"
                      data-testid={`domain-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                      style={{
                        padding: 22, position: "relative", overflow: "hidden",
                        borderLeft: `3px solid ${d.accent}`,
                      }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 8,
                      background: `${d.accent}22`, color: d.accent,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon size={20} />
                    </div>
                    <span className="pf-heading" style={{ fontSize: 20 }}>{d.label}</span>
                  </div>
                  <div className="pf-overline" style={{ fontSize: 9, marginBottom: 8 }}>
                    SECTORS COVERED
                  </div>
                  <p style={{ color: "var(--pf-text-muted)", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                    {d.sectors}
                  </p>
                  <div className="pf-overline" style={{ fontSize: 9, margin: "14px 0 8px" }}>
                    DELIVERABLES
                  </div>
                  <p style={{ color: "var(--pf-text)", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                    {d.capabilities}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4 LAYERS ───────────────────────────────────────── */}
      <section data-testid="section-layers" style={{ borderBottom: "1px solid var(--pf-border)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "72px 28px" }}>
          <div className="pf-overline" style={{ marginBottom: 14 }}>
            FOUR-LAYER AI ENGINE · DETERMINISTIC IN THE CRITICAL PATH
          </div>
          <h2 className="pf-heading" style={{
            fontSize: "clamp(26px, 3.6vw, 38px)", margin: "0 0 28px",
            letterSpacing: "-0.025em",
          }}>
            How a brief becomes a buildable plant.
          </h2>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}>
            {[
              { k: "L1", icon: FileText, t: "NLP Interpreter",
                d: "Parses plain-English briefs into a Process Design Object — the machine-readable spec that drives every later stage." },
              { k: "L2", icon: FlaskConical, t: "Engineering Sims",
                d: "Mass + energy balance, hydraulics, sizing, settlement, traffic & noise. First-principles math — never an LLM in the safety path." },
              { k: "L3", icon: Layers, t: "Drawings + 3D",
                d: "PFD · P&ID with ISA 5.1 + SIS · 3D glTF + IFC4 · DXF · DEXPI XML for downstream AutoCAD / Revit / Navisworks." },
              { k: "L4", icon: Cog, t: "Construction Pack",
                d: "BOQ · CPM Gantt · BBS · Datasheets · EIA · Compliance — every artefact SHA-256 signed and bundled into one ZIP." },
            ].map((f) => {
              const I = f.icon;
              return (
                <div key={f.k} className="pf-card" style={{ padding: 22 }}
                      data-testid={`feat-${f.k}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <I size={18} style={{ color: "var(--pf-accent)" }} />
                    <span className="pf-mono" style={{ color: "var(--pf-accent)", fontSize: 12 }}>{f.k}</span>
                    <span className="pf-heading" style={{ fontSize: 17 }}>{f.t}</span>
                  </div>
                  <p style={{ color: "var(--pf-text-muted)", margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>
                    {f.d}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TIER MODULES ───────────────────────────────────── */}
      <section data-testid="section-tiers" style={{ borderBottom: "1px solid var(--pf-border)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "72px 28px" }}>
          <div className="pf-overline" style={{ marginBottom: 14 }}>
            TIER-1 · TIER-2 · TIER-3 · CLOSED LOOP
          </div>
          <h2 className="pf-heading" style={{
            fontSize: "clamp(26px, 3.6vw, 38px)", margin: "0 0 28px",
            letterSpacing: "-0.025em",
          }}>
            Beyond drawings — every artefact a project actually ships.
          </h2>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 14,
          }}>
            {TIER_FEATURES.map((f) => {
              const I = f.icon;
              return (
                <div key={f.title} className="pf-card" style={{
                  padding: 22, position: "relative", overflow: "hidden",
                }} data-testid={`tier-${f.title.split(" ")[0].toLowerCase().replace(/\W/g, "")}`}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, marginBottom: 14,
                    background: `${f.accent}22`, color: f.accent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <I size={18} />
                  </div>
                  <div className="pf-heading" style={{ fontSize: 16, marginBottom: 6 }}>
                    {f.title}
                  </div>
                  <p style={{ color: "var(--pf-text-muted)", margin: 0, fontSize: 13, lineHeight: 1.55 }}>
                    {f.blurb}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── COMPANION + CTA ────────────────────────────────── */}
      <section data-testid="section-companion" style={{ borderBottom: "1px solid var(--pf-border)" }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto", padding: "72px 28px",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "center",
        }}>
          <div>
            <div className="pf-overline" style={{ marginBottom: 14 }}>
              ENGINEERING IS HUMAN · SO IS CAPEXARA
            </div>
            <h2 className="pf-heading" style={{
              fontSize: "clamp(26px, 3.6vw, 38px)", margin: "0 0 18px",
              letterSpacing: "-0.025em",
            }}>
              An AI companion that remembers how you felt yesterday.
            </h2>
            <p style={{
              color: "var(--pf-text-muted)", fontSize: 16, lineHeight: 1.6, margin: 0,
            }}>
              Stage gates are intense. Capexara Process Industry opens with a soft, time-of-day greeting,
              listens to a one-tap mood check, and adjusts tomorrow's tone based on
              today's. We treat the engineer behind the prompt with the same care we
              put into the engineering itself.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
              <Link to="/register" className="btn btn-primary" data-testid="cta-register">
                <Zap size={14}/> Start free build <ArrowRight size={14}/>
              </Link>
              <Link to="/login" className="btn btn-ghost" data-testid="cta-login">Sign in</Link>
            </div>
          </div>
          <div className="pf-card" style={{
            padding: 24, background: "var(--pf-blueprint)",
            border: "1px solid rgba(242,107,31,0.25)",
          }}>
            <div className="pf-overline" style={{ color: "#FBBC04", marginBottom: 14 }}>
              CAPEXARA · COMPANION
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: "#f7f9fd", margin: 0 }}>
              "Good morning, Anudit. I remember yesterday felt heavy with stress
              weighing on you. I hope the night brought some rest and a bit of space
              from whatever was pressing. How are you feeling as you step into today?"
            </p>
            <div style={{
              display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap",
            }}>
              {["😄 Great", "🎯 Focused", "🙂 Okay", "😪 Tired", "😣 Stressed"].map((m) => (
                <span key={m} style={{
                  padding: "6px 12px", borderRadius: 22, fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.16)",
                  color: "#dfe5f3", background: "rgba(255,255,255,0.04)",
                }}>{m}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer style={{
        padding: "28px", color: "var(--pf-text-dim)",
        fontFamily: "JetBrains Mono", fontSize: 11, textAlign: "center",
      }}>
        © {new Date().getFullYear()} Capexara Process Industry · Agentic AI Design Engine ·
        Process · Infrastructure · Real Estate · Built on Emergent
      </footer>
    </div>
  );
}

function Stat({ n, t }) {
  return (
    <div data-testid={`stat-${t.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
      <div className="pf-heading" style={{
        fontSize: 28, fontWeight: 800, color: "var(--pf-accent)",
        letterSpacing: "-0.02em",
      }}>{n}</div>
      <div className="pf-overline" style={{ fontSize: 10, marginTop: 2 }}>{t}</div>
    </div>
  );
}

function PreviewSvg() {
  return (
    <svg viewBox="0 0 640 360" width="100%" height="320" style={{ display: "block" }}>
      <defs>
        <pattern id="gr" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#00E5FF" strokeOpacity="0.08"/>
        </pattern>
        <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5"
                markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#FFB000"/>
        </marker>
      </defs>
      <rect width="640" height="360" fill="url(#gr)"/>
      <rect x="40" y="110" width="80" height="140" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <text x="80" y="270" fontFamily="JetBrains Mono" fontSize="10" fill="currentColor" textAnchor="middle">T-101 FEED</text>
      <circle cx="200" cy="180" r="22" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <polygon points="188,168 188,192 214,180" fill="currentColor"/>
      <text x="200" y="220" fontFamily="JetBrains Mono" fontSize="10" fill="currentColor" textAnchor="middle">P-101</text>
      <rect x="270" y="130" width="120" height="100" rx="8" fill="none" stroke="#00E5FF" strokeWidth="1.5"/>
      <line x1="270" y1="160" x2="390" y2="160" stroke="#00E5FF" strokeOpacity="0.4"/>
      <line x1="270" y1="200" x2="390" y2="200" stroke="#00E5FF" strokeOpacity="0.4"/>
      <text x="330" y="250" fontFamily="JetBrains Mono" fontSize="10" fill="currentColor" textAnchor="middle">V-101 DESALTER</text>
      <rect x="480" y="110" width="80" height="140" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <text x="520" y="270" fontFamily="JetBrains Mono" fontSize="10" fill="currentColor" textAnchor="middle">T-102 OUT</text>
      <polyline points="120,180 178,180" fill="none" stroke="#FFB000" strokeWidth="1.5" markerEnd="url(#arr)"/>
      <polyline points="222,180 270,180" fill="none" stroke="#FFB000" strokeWidth="1.5" markerEnd="url(#arr)"/>
      <polyline points="390,180 480,180" fill="none" stroke="#FFB000" strokeWidth="1.5" markerEnd="url(#arr)"/>
      <text x="148" y="170" fontFamily="JetBrains Mono" fontSize="9" fill="#FFB000">S-001 100 m³/h</text>
      <text x="240" y="170" fontFamily="JetBrains Mono" fontSize="9" fill="#FFB000">S-002</text>
      <text x="420" y="170" fontFamily="JetBrains Mono" fontSize="9" fill="#FFB000">S-003</text>
      <circle cx="160" cy="180" r="10" fill="none" stroke="#FFB000"/>
      <text x="160" y="183" fontFamily="JetBrains Mono" fontSize="8" fill="#FFB000" textAnchor="middle">FT</text>
      <circle cx="430" cy="180" r="10" fill="none" stroke="#FFB000"/>
      <text x="430" y="183" fontFamily="JetBrains Mono" fontSize="8" fill="#FFB000" textAnchor="middle">PT</text>
    </svg>
  );
}
